<!-- 
  DatePicker provides a date input field (DD/MM/YYYY) with a drop-down calendar. Only valid date values are allowed.

  PROPS:
    label: Label string, e.g. "Date of birth"

    date: Current date value. Must match format specified

    format: Specifies what format the "date" property should use (either 'dmy', 'ymd' or 'iso'). Defaults to 'iso'.

    isRequired: True if a valid date must be present 
    
    isPastDate: True if only past dates are allowed 
    
    isFutureDate: True if only future dates are allowed (will be overridden by isPastDate)

    rules: Optional array of Vuetify validation rules

    validateOnBlur: Optional pass through of validate-on-blur

  EVENTS:
    @change(date) - Fires when a date is entered, cleared or selected. "date" parameter contains a date string, formatted according to the "format" property.
-->

<template>
  <v-menu
    v-model="isDatePickerOpen"
    :open-on-click="false"
    :close-on-content-click="false"
    offset-y
    max-width="290px"
    min-width="290px"
  >
    <template v-slot:activator="{ on }">
      <v-text-field
        class="ads-date-picker"
        v-model.trim="inputValue"
        :rules="getRules"
        :label="label"
        placeholder="DD/MM/YYYY"
        @change="onDateChange"
        v-on="on"
        outlined
        maxlength="10"
        ref="input"
        :validate-on-blur="validateOnBlur"
      >
        <template slot="append">
          <v-icon class="ads-navy" @click="openDatePicker" tabindex="-1"
            >event</v-icon
          >
        </template>
      </v-text-field>
    </template>
    <v-date-picker
      v-model="calendarDate"
      @change="onCalendarChange"
      no-title
      @input="isDatePickerOpen = false"
    ></v-date-picker>
  </v-menu>
</template>

<script>
export default {
  name: 'DatePicker',

  props: {
    label: String,
    date: String,
    isRequired: Boolean,
    format: {
      default() {
        return 'iso'
      },
      type: String // The format of the underlying field value (either 'dmy', 'ymd' or 'iso'),
    },
    rules: Array,
    isFutureDate: Boolean,
    isPastDate: Boolean,
    validateOnBlur: {
      default() {
        return true
      },
      type: Boolean
    }
  },

  data() {
    return {
      inputValue: null, // Always DD/MM/YYYY format
      calendarDate: null, // Always YYYY-MM-DD format
      isDatePickerOpen: false, // Controls the visibility of the calendar popup
      dateObject: null // Populated whenever a valid date is set
    }
  },

  mounted() {
    this.getDate()
  },

  watch: {
    date() {
      this.getDate()
    }
  },

  computed: {
    getRules() {
      let rules = this.rules ? this.rules.map(r => r) : [] // Gets copy of existing rules from prop
      if (this.isRequired) {
        rules.push(() => !!this.dateObject || 'A valid date is required')
      }
      if (this.isPastDate) {
        rules.push(
          () =>
            !this.dateObject ||
            this.dateObject < new Date() ||
            'Must be a past date'
        )
      } else if (this.isFutureDate) {
        rules.push(
          () =>
            !this.dateObject ||
            this.dateObject > new Date() ||
            'Must be a future date'
        )
      }
      return rules
    }
  },

  methods: {
    openDatePicker() {
      this.isDatePickerOpen = true
    },

    getDate() {
      this.inputValue = this.date
      this.calendarDate = this.formatTo(this.inputValue, 'iso')
      this.dateObject = this.calendarDate ? new Date(this.calendarDate) : null
    },

    onCalendarChange() {
      this.$refs.input.focus()
      let [year, month, day] = this.calendarDate.split('-')
      this.$emit('change', this.formatDateOutput(day, month, year))
    },

    onDateChange() {
      // Formats and validates the date string entered

      // Split into day/month/year parts (using any separators)
      let [day, month, year] = this.inputValue
        .replace(/[^0-9]/g, '/')
        .split('/')

      // Pad single digit days/months
      day = day ? day.padStart(2, '0') : null
      month = month ? month.padStart(2, '0') : null

      // Convert any 2 digit years to this century
      if (year && year.length == 2) {
        year = '20' + year
      }

      // Create date object
      let date = new Date(`${year}-${month}-${day}`)
      if (date.getDate() !== Number(day)) {
        // If date object has different day value (or no date at all), this means our original date was invalid (e.g. "31/02/2020")
        this.$emit('change', this.inputValue)
      } else {
        this.inputValue = `${day}/${month}/${year}`
        this.$emit('change', this.formatDateOutput(day, month, year))
      }
      setTimeout(() => this.$refs.input.validate(), 0) // Update validation on next tick, otherwise it won't get the new value
    },

    formatDateOutput(day, month, year) {
      // Formats date output as dmy, ymd or iso
      let parts
      if (this.format === 'dmy') {
        parts = [day, month, year]
      } else {
        parts = [year, month, day]
      }
      return parts.join(this.format === 'iso' ? '-' : '/')
    },

    formatTo(date, format) {
      if (date && date.length === 10) {
        let day, month, year
        if (this.format === 'dmy') {
          ;[day, month, year] = date.split('/')
        } else if (this.format === 'ymd') {
          ;[year, month, day] = date.split('/')
        } else if (this.format === 'iso') {
          ;[year, month, day] = date.split('-')
        }
        if (format === 'dmy') {
          return `${day}/${month}/${year}`
        } else if (format === 'ymd') {
          return `${year}/${month}/${day}`
        }
        return `${year}-${month}-${day}`
      }
      return ''
    }
  }
}
</script>

<style lang="scss" scoped></style>
